import {IconCurrencyBitcoin, IconCurrencyDollar, IconSearch} from "@tabler/icons-react";
import {Modal} from "@ui";
import PropTypes from "prop-types";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import useCurrencyStore from "../../../store/useCurrency.store";
import CustomSwitch from "../CustomSwitch";
import CustomTabs from "../CustomTabs";
import styles from "./CurrencyManagementModal.module.css";

const filterCurrencies = (list, searchValue, t) => {
  if (!list) return [];
  const term = (searchValue || "").toLowerCase();
  return list.filter(currency => {
    const translatedName = t(`currency${currency.id}`).toLowerCase();
    const translatedSymbol = t(`currencySymbol${currency.id}`).toLowerCase();
    const currencyId = (currency.id || "").toLowerCase();

    const basicMatch = translatedName.includes(term) || translatedSymbol.includes(term) || currencyId.includes(term);

    const aliasMap = {
      VES: ["bolivares", "venezuela", "bs"],
      USD: ["dollars", "dollar", "dolares", "dolar"],
      USDT: ["tether", "usdt"],
    };

    const aliases = aliasMap[currency.id] ?? [];
    return basicMatch || aliases.some(alias => alias.includes(term));
  });
};

const CurrencyList = ({currencyList, searchValue}) => {
  const {t} = useTranslation();
  const {selectedCurrency, toggleCurrencyEnabled, setSelectedCurrency} = useCurrencyStore();

  const filtered = filterCurrencies(currencyList, searchValue, t);

  return (
    <div className={styles.currencyList}>
      {filtered.map(currency => {
        const isSelected = selectedCurrency?.id === currency.id;

        const handleCurrencyClick = () => {
          if (currency.enabled) setSelectedCurrency(currency.id);
        };

        return (
          <div
            key={currency.id}
            className={`${styles.currencyItem} ${isSelected ? styles.selected : ""}`}
            onClick={handleCurrencyClick}
            data-disabled={!currency.enabled}
          >
            <div className={styles.currencyLeft}>
              <div className={styles.currencyIcon}>{t(`currencySymbol${currency.id}`)}</div>
              <span className={styles.currencyName}>{t(`currency${currency.id}`)}</span>
            </div>
            <CustomSwitch
              checked={currency.enabled}
              onChange={() => toggleCurrencyEnabled(currency.id)}
              disabled={currency.isDefault}
              onClick={e => e.stopPropagation()}
            />
          </div>
        );
      })}
      {filtered.length === 0 && (
        <div className={styles.emptyState}>
          <span>{t("currencyNoResults")}</span>
        </div>
      )}
    </div>
  );
};

CurrencyList.propTypes = {
  currencyList: PropTypes.array.isRequired,
  searchValue: PropTypes.string,
};

const CurrencyManagementModal = () => {
  const {t} = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("fiat");

  const {currencies, isManagementModalVisible, hideManagementModal} = useCurrencyStore();

  const fiatCurrencies = currencies.filter(c => c.type === "fiat");
  const cryptoCurrencies = currencies.filter(c => c.type === "crypto");

  const tabItems = [
    {
      key: "fiat",
      label: (
        <>
          <IconCurrencyDollar
            size={16}
            stroke={2}
          />
          <span>{t("currencyManageFiatTab")}</span>
        </>
      ),
      children: (
        <CurrencyList
          currencyList={fiatCurrencies}
          searchValue={searchValue}
        />
      ),
    },
    {
      key: "crypto",
      label: (
        <>
          <IconCurrencyBitcoin
            size={16}
            stroke={2}
          />
          <span>{t("currencyManageCryptoTab")}</span>
        </>
      ),
      children: (
        <CurrencyList
          currencyList={cryptoCurrencies}
          searchValue={searchValue}
        />
      ),
    },
  ];

  return (
    <Modal
      title={t("currencyManageTitle")}
      open={isManagementModalVisible}
      onCancel={hideManagementModal}
      footer={null}
      width={480}
      className={styles.managementModal}
      centered
    >
      <div className={styles.modalContent}>
        <p className={styles.subtitle}>{t("currencyManagementNote")}</p>

        <div className={styles.searchBar}>
          <IconSearch
            size={16}
            stroke={2}
            className={styles.searchIcon}
          />
          <input
            type="text"
            placeholder={t("currencySearch")}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <CustomTabs
          items={tabItems}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>
    </Modal>
  );
};

export default CurrencyManagementModal;
